import { Avatar } from "@mui/material";
import { ReactElement } from "react";
import { Datagrid, FunctionField, TextField, useTranslate } from "react-admin";

import { PractitionerStatusEnum } from "../generated/graphql";
import { getAccessStatus } from "../lib/accessStatus";
import { PRACTITIONER_LOCATION_SCOPE, PractitionerRecord } from "../types/practitioner";
import { AccessStatusChip } from "./fields/AccessStatusChip";
import { SourceSystemChip } from "./fields/SourceSystemChip";

interface PractitionerDataGridProps {
  showLocationColumn?: boolean;
  showAccessStatus?: boolean;
  bulkActionButtons?: ReactElement | false;
}

export const PractitionerDataGrid = ({
  showLocationColumn = true,
  showAccessStatus = false,
  bulkActionButtons = false,
}: PractitionerDataGridProps) => {
  const translate = useTranslate();

  return (
    <Datagrid
      rowClick={"show"}
      bulkActionButtons={bulkActionButtons}
      sx={{
        "& .RaBulkActionsToolbar-toolbar:not(.RaBulkActionsToolbar-collapsed)": {
          position: "relative",
          transform: "none",
        },
      }}
    >
      <FunctionField
        label={translate("avatar")}
        render={(record: PractitionerRecord) => {
          const isDeactivated = record.status === PractitionerStatusEnum.Inactive;
          return (
            <Avatar
              src={record.avatarUrl || undefined}
              alt={record.name}
              sx={{
                filter: isDeactivated ? "grayscale(100%)" : "none",
                opacity: isDeactivated ? 0.6 : 1,
              }}
            />
          );
        }}
      />
      <TextField source="firstName" sortable={false} label={translate("firstName")} />
      <TextField source="lastName" sortable={false} label={translate("lastName")} />
      <TextField source="employeeNumber" sortable={false} label={translate("workerNumber")} />
      <FunctionField
        label="Quelle"
        render={(record: PractitionerRecord) => (
          <SourceSystemChip record={record} translatePrefix="Practitioner.fields.sourceSystem" />
        )}
      />
      <TextField source="emailAddress" sortable={false} label={translate("email")} />
      {showLocationColumn && (
        <FunctionField
          source="locations"
          sortable={false}
          label={translate("locations")}
          render={(record: PractitionerRecord) =>
            record.locationScope === PRACTITIONER_LOCATION_SCOPE.All
              ? translate("practitionerLocationScope.all")
              : record.locations?.length
                ? record.locations
                    .map(loc => (loc.type ? `${loc.name} (${translate(`locationType.${loc.type}`)})` : loc.name))
                    .join(", ")
                : translate("noLocations")
          }
        />
      )}
      {showAccessStatus ? (
        <FunctionField
          label={translate("status")}
          render={(record: PractitionerRecord) => (
            <AccessStatusChip accessStatus={record.accessStatus ?? getAccessStatus(record.status, record.authStatus)} />
          )}
          sortable={false}
        />
      ) : (
        <FunctionField
          label={translate("status")}
          render={(record: PractitionerRecord) => translate(`practitionerStatus.${record.status}`)}
          sortable={false}
        />
      )}
    </Datagrid>
  );
};
